/** Published versions: preview, make live (rollback) or restore into the drafts. Route /sites/:id/versions */
import React, { useState } from 'react';
import { Alert, Button, Card, Checkbox, Dropdown, Modal, Space, Table, Tag, Typography, message } from 'antd';
import { EditOutlined, EyeOutlined, MoreOutlined, RollbackOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { fmtDate, listOf, toastErr, useLoad, useSite } from './common';

const { Text } = Typography;

const Versions = () => {
    const { t } = useTranslation();
    const { api, reload: reloadSite } = useSite();
    const { data, loading, reload } = useLoad(() => api.get('/versions'), [api]);
    const [busy, setBusy] = useState(null);
    const [draftOf, setDraftOf] = useState(null); // version picked for "restore to draft"
    const [unpublishExtra, setUnpublishExtra] = useState(false);
    const [result, setResult] = useState(null);

    const activate = (v) => Modal.confirm({
        title: t('Make version {{v}} live?', { v: v.version }),
        content: t('Your drafts stay as they are. The next publish replaces this version again.'),
        okText: t('Make live'),
        onOk: async () => {
            setBusy(v.version);
            try {
                await api.post(`/versions/${v.version}/activate`);
                message.success(t('Version {{v}} is live', { v: v.version }));
                reload();
                reloadSite();
            } catch (e) { toastErr(e); } finally { setBusy(null); }
        },
    });
    const preview = async (v) => {
        const w = window.open('', '_blank'); // opened now: a window opened after the request would be blocked
        setBusy(v.version);
        try {
            const r = await api.post(`/versions/${v.version}/preview-link`);
            if (w) w.location.href = r.url; else window.open(r.url, '_blank', 'noopener');
        } catch (e) { if (w) w.close(); toastErr(e); } finally { setBusy(null); }
    };
    const restoreDraft = async () => {
        const v = draftOf;
        setBusy(v.version);
        try {
            const r = await api.post(`/versions/${v.version}/restore-draft`, { unpublish_extra: unpublishExtra });
            setDraftOf(null);
            setResult(r);
            reloadSite();
        } catch (e) { toastErr(e); } finally { setBusy(null); }
    };

    return (
        <Card title={t('Versions')} size="small">
            <Table rowKey="version" size="small" loading={loading} dataSource={listOf(data)} pagination={{ pageSize: 20, hideOnSinglePage: true }} scroll={{ x: 560 }}
                columns={[
                    { title: t('Version'), dataIndex: 'version', width: 100, render: (v, r) => <>v{v} {r.active && <Tag color="green">{t('Live', { context: 'shop' })}</Tag>}</> },
                    { title: t('Note', { context: 'shop' }), dataIndex: 'note', render: (v) => v || '-' },
                    { title: t('Published'), dataIndex: 'created_at', width: 180, render: (v) => fmtDate(v) },
                    {
                        title: '', key: 'a', width: 190, align: 'right', render: (_, r) => (
                            <Space size={4}>
                                <Button size="small" icon={<EyeOutlined />} loading={busy === r.version} onClick={() => preview(r)}>{t('Preview')}</Button>
                                <Dropdown trigger={['click']} menu={{
                                    items: [
                                        { key: 'draft', icon: <EditOutlined />, label: t('Restore to draft') },
                                        ...(r.active ? [] : [{ key: 'live', icon: <RollbackOutlined />, label: t('Make live') }]),
                                    ],
                                    onClick: ({ key }) => {
                                        if (key === 'live') { activate(r); return; }
                                        setUnpublishExtra(false);
                                        setDraftOf(r);
                                    },
                                }}>
                                    <Button size="small" icon={<MoreOutlined />} aria-label={t('More')} />
                                </Dropdown>
                            </Space>
                        ),
                    },
                ]} />
            <Modal open={!!draftOf} title={t('Restore version {{v}} to draft?', { v: draftOf?.version })} okText={t('Restore to draft')}
                confirmLoading={!!draftOf && busy === draftOf.version} onCancel={() => setDraftOf(null)} onOk={restoreDraft} destroyOnHidden>
                <Text type="secondary">{t('Pages and global blocks go back to this version as drafts. The live site does not change until you publish.')}</Text>
                <div style={{ marginTop: 12 }}>
                    <Checkbox checked={unpublishExtra} onChange={(e) => setUnpublishExtra(e.target.checked)}>{t('Set pages added since then to draft')}</Checkbox>
                </div>
            </Modal>
            <Modal open={!!result} title={t('Drafts restored')} onCancel={() => setResult(null)} footer={<Button type="primary" onClick={() => setResult(null)}>{t('Done')}</Button>}>
                {result && (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Text>{t('{{n}} pages restored, {{m}} recreated', { n: result.restored, m: result.created })}</Text>
                        {result.extra?.length > 0 && (
                            <Alert type="info" showIcon message={result.unpublished_extra ? t('Set to draft') : t('Not in this version (kept)')}
                                description={result.extra.map((p) => p.title || p.path).join(', ')} />
                        )}
                        {result.failed?.length > 0 && <Alert type="warning" showIcon message={t('Not restored')} description={result.failed.map((f) => `${f.path}: ${f.error}`).join('\n')} />}
                    </Space>
                )}
            </Modal>
        </Card>
    );
};

export default Versions;
